import { useDeferredValue, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Inbox, MailPlus, Plus, RefreshCw, Send, ShieldCheck } from "lucide-react";
import type { MailAttachment, MailboxAddressAvailability, MailDomainSummary, MailMessage, MailMessageSummary, Mailbox, MailboxWorkspace } from "../../shared/types";
import { PageHeader } from "../components/app-shell";
import { MailMessageReader } from "../components/mail-message-reader";
import { Button, Empty, ErrorState, Field, Loading, Tag, useToast } from "../components/ui";
import { useSession } from "../context/session";
import { api, jsonBody } from "../lib/api";

function expiryLabel(mailbox: Mailbox): string {
  if (mailbox.permanent) return "永不过期";
  if (!mailbox.expiresAt) return mailbox.status === "released" ? "已释放" : "—";
  const hours = Math.max(0, Math.ceil((Date.parse(mailbox.expiresAt) - Date.now()) / 3_600_000));
  return hours < 24 ? `${hours} 小时后过期` : `${Math.ceil(hours / 24)} 天后过期`;
}

export function MailPage() {
  const { user } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messageId, setMessageId] = useState<string | null>(null);
  const [messagePage, setMessagePage] = useState(1);
  const [mode, setMode] = useState<"read" | "compose">("read");
  const [createOpen, setCreateOpen] = useState(false);
  const [domainId, setDomainId] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [permanent, setPermanent] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const canCustomMailbox = user?.role === "super_admin" || user?.role === "admin" || user?.tier === "vip";
  const deferredLocalPart = useDeferredValue(localPart.trim().toLowerCase());

  const mailboxesQuery = useQuery({
    queryKey: ["mailboxes"],
    queryFn: () => api<Mailbox[]>("/api/mailboxes"),
    refetchInterval: 5000,
  });
  const domainsQuery = useQuery({
    queryKey: ["mail-domains-available"],
    queryFn: () => api<MailDomainSummary[]>("/api/mail/domains"),
  });
  const effectiveSelectedId = selectedId && mailboxesQuery.data?.some((mailbox) => mailbox.id === selectedId)
    ? selectedId
    : mailboxesQuery.data?.[0]?.id ?? null;
  const effectiveDomainId = domainId || domainsQuery.data?.[0]?.id || "";
  const availabilityQuery = useQuery({
    queryKey: ["mailbox-address-availability", effectiveDomainId, deferredLocalPart],
    queryFn: () => {
      const query = new URLSearchParams({ domainId: effectiveDomainId, localPart: deferredLocalPart });
      return api<MailboxAddressAvailability>(`/api/mail/address-availability?${query.toString()}`);
    },
    enabled: Boolean(
      createOpen &&
      canCustomMailbox &&
      effectiveDomainId &&
      deferredLocalPart &&
      /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/.test(deferredLocalPart),
    ),
    staleTime: 5_000,
    retry: false,
  });
  const workspaceQuery = useQuery({
    queryKey: ["mailbox-workspace", effectiveSelectedId, messagePage],
    queryFn: () => api<MailboxWorkspace>(`/api/mailboxes/${effectiveSelectedId}?page=${messagePage}&pageSize=20`),
    enabled: Boolean(effectiveSelectedId),
    refetchInterval: 5000,
  });
  const effectiveMessageId = messageId && workspaceQuery.data?.messages.some((message) => message.id === messageId)
    ? messageId
    : workspaceQuery.data?.messages[0]?.id ?? null;
  const messageQuery = useQuery({
    queryKey: ["mail-message", effectiveSelectedId, effectiveMessageId],
    queryFn: () => api<MailMessage>(`/api/mailboxes/${effectiveSelectedId}/messages/${effectiveMessageId}`),
    enabled: Boolean(effectiveSelectedId && effectiveMessageId && mode === "read"),
  });

  const selectedMailbox = useMemo(
    () => mailboxesQuery.data?.find((mailbox) => mailbox.id === effectiveSelectedId) ?? workspaceQuery.data?.mailbox ?? null,
    [effectiveSelectedId, mailboxesQuery.data, workspaceQuery.data],
  );

  const createMutation = useMutation({
    mutationFn: () => api<Mailbox>("/api/mailboxes", {
      method: "POST",
      ...jsonBody({ domainId: effectiveDomainId, ...(localPart.trim() ? { localPart: localPart.trim() } : {}), permanent }),
    }),
    async onSuccess(mailbox) {
      await queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      setSelectedId(mailbox.id);
      setMessageId(null);
      setMessagePage(1);
      setCreateOpen(false);
      setLocalPart("");
      setPermanent(false);
      toast.push("邮箱地址已创建");
    },
    onError(error) { toast.fromError(error); },
  });

  const extendMutation = useMutation({
    mutationFn: (makePermanent: boolean) => api<Mailbox>(`/api/mailboxes/${effectiveSelectedId}/extend`, {
      method: "POST",
      ...jsonBody({ permanent: makePermanent }),
    }),
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["mailboxes"] }),
        queryClient.invalidateQueries({ queryKey: ["mailbox-workspace", effectiveSelectedId] }),
      ]);
      toast.push("地址时效已更新");
    },
    onError(error) { toast.fromError(error); },
  });

  const sendMutation = useMutation({
    mutationFn: () => api<MailMessageSummary>(`/api/mailboxes/${effectiveSelectedId}/send`, {
      method: "POST",
      ...jsonBody({ to, subject, text }),
    }),
    async onSuccess(message) {
      await queryClient.invalidateQueries({ queryKey: ["mailbox-workspace", effectiveSelectedId] });
      setMode("read");
      setMessageId(message.id);
      setTo("");
      setSubject("");
      setText("");
      toast.push("邮件已提交发送");
    },
    onError(error) { toast.fromError(error); },
  });

  const openMessage = async (id: string) => {
    setMessageId(id);
    setMode("read");
    await api<{ readAt: string }>(`/api/mailboxes/${effectiveSelectedId}/messages/${id}/read`, { method: "PATCH" }).catch(() => undefined);
    void queryClient.invalidateQueries({ queryKey: ["mailbox-workspace", effectiveSelectedId] });
  };

  const submitCreate = (event: FormEvent) => {
    event.preventDefault();
    createMutation.mutate();
  };

  const submitSend = (event: FormEvent) => {
    event.preventDefault();
    sendMutation.mutate();
  };

  const loadAttachment = async (attachment: MailAttachment): Promise<Blob> => {
    const response = await fetch(attachment.downloadUrl, { credentials: "same-origin" });
    if (!response.ok) throw new Error("附件下载失败");
    return response.blob();
  };

  const downloadAttachment = async (attachment: MailAttachment) => {
    const href = URL.createObjectURL(await loadAttachment(attachment));
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = attachment.filename;
    anchor.click();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="page mail-page">
      <PageHeader
        eyebrow="TEMPORARY MAIL · 临时邮箱"
        title="收件与发信工作台"
        body="多地址并存、实时收件；发信能力由域组策略和 Cloudflare Email Sending 共同控制。"
        actions={<Button variant="primary" onClick={() => setCreateOpen((value) => !value)}><Plus size={15} />新建地址</Button>}
      />
      {createOpen ? (
        <form className="mail-create-bar" onSubmit={submitCreate}>
          <Field
            label="地址前缀 · 留空则随机"
            hint={canCustomMailbox
              ? deferredLocalPart && availabilityQuery.data
                ? <Tag tone={availabilityQuery.data.available ? "vip" : "outline"}>{availabilityQuery.data.available ? "名称可用" : availabilityQuery.data.reason}</Tag>
                : "VIP 与管理员可指定名称；同一域名下不会重复分配。"
              : "普通用户由系统随机分配；升级 VIP 后可指定名称。"}
          >
            <input className="input" value={localPart} onChange={(event) => setLocalPart(event.target.value)} placeholder={canCustomMailbox ? "quiet-fox-84" : "系统随机分配"} disabled={!canCustomMailbox} />
          </Field>
          <Field label="邮件域名">
            <select className="input select" value={effectiveDomainId} onChange={(event) => setDomainId(event.target.value)} required>
              {domainsQuery.data?.map((domain) => <option key={domain.id} value={domain.id}>@{domain.domain} · {domain.groupName}{domain.vipOnly ? " · VIP" : ""}</option>)}
            </select>
          </Field>
          {user?.role !== "user" ? <label className="mail-permanent-check"><input type="checkbox" checked={permanent} onChange={(event) => setPermanent(event.target.checked)} />永不过期</label> : null}
          <Button variant="primary" type="submit" disabled={createMutation.isPending || availabilityQuery.isFetching || !effectiveDomainId || Boolean(deferredLocalPart && availabilityQuery.data && !availabilityQuery.data.available)}>{createMutation.isPending ? "创建中" : "创建地址"}</Button>
        </form>
      ) : null}

      {mailboxesQuery.isLoading ? <Loading label="载入邮箱地址" /> : mailboxesQuery.error ? <ErrorState error={mailboxesQuery.error} retry={() => void mailboxesQuery.refetch()} /> : (
        <section className="mail-workspace-grid">
          <aside className="mailbox-list-panel">
            <header><span className="eyebrow">我的地址 · {mailboxesQuery.data?.length ?? 0}</span><Button variant="primary" className="button-square" onClick={() => setCreateOpen(true)} aria-label="新建地址"><Plus size={14} /></Button></header>
            {mailboxesQuery.data?.length ? mailboxesQuery.data.map((mailbox) => (
              <button type="button" key={mailbox.id} className={mailbox.id === effectiveSelectedId ? "mailbox-list-item mailbox-list-item--active" : "mailbox-list-item"} onClick={() => { setSelectedId(mailbox.id); setMessageId(null); setMessagePage(1); setMode("read"); }}>
                <strong>{mailbox.address}</strong>
                <span><Tag tone={mailbox.permanent ? "neutral" : mailbox.status === "active" ? "accent" : "outline"}>{expiryLabel(mailbox)}</Tag>{mailbox.unreadCount ? <small>{mailbox.unreadCount} 封未读</small> : null}</span>
              </button>
            )) : <Empty title="还没有地址" body="新建一个临时邮箱开始收信。" />}
            <footer>地址释放后进入冷却，避免下一位持有者收到上一位的邮件。</footer>
          </aside>

          <section className="mail-message-list-panel">
            <header>
              <div><strong>{selectedMailbox?.address ?? "选择一个地址"}</strong><span className="eyebrow">INBOX · 收件箱</span></div>
              <span className="live-state"><i />LIVE</span>
              {selectedMailbox ? <Button variant="ghost" onClick={() => extendMutation.mutate(false)} disabled={extendMutation.isPending}><RefreshCw size={13} />延长</Button> : null}
            </header>
            {workspaceQuery.isLoading ? <Loading label="同步收件箱" /> : workspaceQuery.error ? <ErrorState error={workspaceQuery.error} retry={() => void workspaceQuery.refetch()} /> : workspaceQuery.data?.messages.length ? workspaceQuery.data.messages.map((message) => (
              <button type="button" key={message.id} className={message.id === effectiveMessageId ? "console-mail-row console-mail-row--active" : "console-mail-row"} onClick={() => void openMessage(message.id)}>
                <span><strong>{message.senderName || message.senderAddress}</strong>{!message.readAt && message.direction === "inbound" ? <i /> : null}{message.verificationCode ? <code className="mail-list-code">{message.verificationCode}</code> : null}</span>
                <span>{message.subject}</span>
                <small>{new Date(message.createdAt).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</small>
              </button>
            )) : <Empty title="收件箱为空" body="保持页面打开，邮件到达后每 5 秒自动同步。" />}
            {workspaceQuery.data ? <footer className="pagination mail-message-pagination"><span>第 {workspaceQuery.data.pagination.page} / {workspaceQuery.data.pagination.totalPages} 页 · 共 {workspaceQuery.data.pagination.total} 封</span><div><Button disabled={messagePage <= 1} onClick={() => { setMessageId(null); setMessagePage((value) => value - 1); }}>← 上一页</Button><Button disabled={messagePage >= workspaceQuery.data.pagination.totalPages} onClick={() => { setMessageId(null); setMessagePage((value) => value + 1); }}>下一页 →</Button></div></footer> : <footer>邮件按域组留存策略自动清理 · 默认 30 天</footer>}
          </section>

          <section className="mail-content-panel">
            <header>
              <div className="segments">
                <button type="button" className={mode === "read" ? "segment segment--active" : "segment"} onClick={() => setMode("read")}>阅读 <span>READ</span></button>
                <button type="button" className={mode === "compose" ? "segment segment--active" : "segment"} onClick={() => setMode("compose")} disabled={!selectedMailbox?.outboundAllowed}>写信 <span>COMPOSE</span></button>
              </div>
              {selectedMailbox?.outboundAllowed ? <Tag tone="neutral">域组已授权发信</Tag> : <Tag tone="outline">仅收信</Tag>}
            </header>
            {mode === "compose" ? (
              <form className="mail-compose" onSubmit={submitSend}>
                <Field label="发件地址 · FROM"><input className="input" value={selectedMailbox?.address ?? ""} readOnly /></Field>
                <Field label="收件人 · TO"><input className="input" type="email" value={to} onChange={(event) => setTo(event.target.value)} required /></Field>
                <Field label="主题 · SUBJECT"><input className="input" value={subject} onChange={(event) => setSubject(event.target.value)} required /></Field>
                <Field label="正文 · MESSAGE"><textarea className="input textarea mail-compose__body" value={text} onChange={(event) => setText(event.target.value)} required /></Field>
                <div className="mail-compose__actions"><Button variant="primary" type="submit" disabled={sendMutation.isPending}><Send size={14} />{sendMutation.isPending ? "发送中" : "发送"}</Button><span>今日已发 {selectedMailbox?.sentCount ?? 0} 封</span></div>
              </form>
            ) : messageQuery.isLoading ? <Loading label="打开邮件" /> : messageQuery.error ? <ErrorState error={messageQuery.error} retry={() => void messageQuery.refetch()} /> : messageQuery.data ? (
              <div className="console-message-detail">
                <MailMessageReader
                  key={messageQuery.data.id}
                  message={messageQuery.data}
                  showHeaders
                  onCopyCode={(code) => void navigator.clipboard.writeText(code).then(() => toast.push("验证码已复制"))}
                  onLoadInlineAttachment={loadAttachment}
                  onDownloadAttachment={(attachment) => void downloadAttachment(attachment).catch((error) => toast.fromError(error))}
                />
              </div>
            ) : <div className="mail-reader-empty"><Inbox size={28} /><strong>选择一封邮件</strong><span>邮件正文、验证码与附件会显示在这里。</span></div>}
            <footer><ShieldCheck size={14} /><span>邮件正文采用隔离渲染；原始信头仅向登录的邮箱所有者与管理员开放。</span>{user?.role !== "user" && selectedMailbox && !selectedMailbox.permanent ? <Button variant="ghost" onClick={() => extendMutation.mutate(true)}>设为永不过期</Button> : null}</footer>
          </section>
        </section>
      )}
      <section className="mail-security-note"><MailPlus size={18} /><div><strong>Cloudflare 原生邮件链路</strong><p>入站由 Email Routing 触发 Worker，正文进入 D1，附件进入私有 R2；出站只对已授权域组开放。</p></div></section>
    </div>
  );
}
